import { Fragment, type ReactNode } from "react";
import {
  parseMarkdown,
  type MarkdownBlock,
  type MarkdownInline,
} from "../markdown";

// Renders the agent's Markdown as React nodes. No HTML is injected, so model
// output cannot smuggle markup or scripts into the editor.

const renderInline = (
  nodes: MarkdownInline[],
  keyPrefix: string,
): ReactNode[] =>
  nodes.map((node, index) => {
    const key = `${keyPrefix}-${index}`;
    switch (node.type) {
      case "text":
        return <Fragment key={key}>{node.value}</Fragment>;
      case "strong":
        return (
          <strong className="font-semibold" key={key}>
            {renderInline(node.children, key)}
          </strong>
        );
      case "em":
        return <em key={key}>{renderInline(node.children, key)}</em>;
      case "code":
        return (
          <code
            className="rounded bg-hazumu-canvas px-1 py-0.5 font-mono text-[0.85em]"
            key={key}
          >
            {node.value}
          </code>
        );
      case "link":
        return (
          <a
            className="font-medium text-hazumu-terracotta underline underline-offset-2"
            href={node.href}
            key={key}
            rel="noreferrer noopener"
            target="_blank"
          >
            {renderInline(node.children, key)}
          </a>
        );
      case "br":
        return <br key={key} />;
    }
  });

const renderBlock = (block: MarkdownBlock, index: number): ReactNode => {
  const key = `block-${index}`;
  switch (block.type) {
    case "paragraph":
      return (
        <p className="whitespace-pre-wrap" key={key}>
          {renderInline(block.children, key)}
        </p>
      );
    case "heading":
      return (
        <p className="font-semibold" key={key}>
          {renderInline(block.children, key)}
        </p>
      );
    case "list": {
      const List = block.ordered ? "ol" : "ul";
      return (
        <List
          className={block.ordered ? "list-decimal pl-4" : "list-disc pl-4"}
          key={key}
        >
          {block.items.map((item, itemIndex) => (
            <li key={`${key}-${itemIndex}`}>
              {renderInline(item, `${key}-${itemIndex}`)}
            </li>
          ))}
        </List>
      );
    }
    case "codeBlock":
      return (
        <pre
          className="overflow-x-auto rounded-lg bg-hazumu-canvas p-2"
          key={key}
        >
          <code className="font-mono text-[0.85em]">{block.value}</code>
        </pre>
      );
    case "quote":
      return (
        <blockquote
          className="border-l-2 border-hazumu-clay pl-2 text-hazumu-muted"
          key={key}
        >
          {renderInline(block.children, key)}
        </blockquote>
      );
    case "rule":
      return <hr className="border-hazumu-border" key={key} />;
  }
};

export const MarkdownText = ({ content }: { content: string }) => (
  <div className="space-y-1.5">{parseMarkdown(content).map(renderBlock)}</div>
);
